import { reactive, ref, watch, computed } from 'vue';
import { useStore, useContext } from '@nuxtjs/composition-api';
import { useVuelidate } from '@vuelidate/core';
import {
  useForm,
  useCategory,
  useModalDialog,
  useValidationRules,
} from '~/composables';
import ServiceFormDpdFields from './ServiceFormDpdFields.vue';
import ServiceFormCourierDeliveryFields from './ServiceFormCourierDeliveryFields.vue';
import ServiceFormNovaPoshtaFields from './ServiceFormNovaPoshtaFields.vue';


export default {
  __name: 'ServiceForm',
  setup(__props) {

const {
  submit,
  formPending,
  recaptcha: $recaptcha,
  isSubmitDisabled,
} = useForm({
  confirm: false,
});
const { nuxtState, $gtm } = useContext();
const store = useStore();
const categorySlug = ref('');

const { category, fetch: fetchCategory } = useCategory(
  categorySlug,
  { limit: 10, includes: ['branch', 'children'], 'order[popular]': 'desc' },
  'service-form',
);

const categories = [
  { id: 'tablet-pc', name: 'Tablets' },
  { id: 'video-conferencing-systems', name: 'Video Conferencing Systems' },
];

const {
  isOpen: infoModalIsOpen,
  open: openInfoModal,
  close: closeInfoModal,
} = useModalDialog('info');

const config = store.getters['config/getConfig'];
const lang = store.getters['config/getActiveLanguage'];

const isFormStartEventSent = ref(false);

const selectedAddress = ref('');
const countriesForService = ref([]);

const DELIVERY_AGENT = {
  DPD: 'pickup',
  COURIER: 'courier',
  NOVA_POSHTA: 'novaposhta',
};

const deliveryAgents = [
  { id: DELIVERY_AGENT.DPD, name: 'Pick up point' },
  { id: DELIVERY_AGENT.COURIER, name: 'Courier delivery' },
  { id: DELIVERY_AGENT.NOVA_POSHTA, name: 'Нова Пошта' },
];

const DEFAULT_FORM_DATA = {
  site: config.domain,
  lang,
  name: '',
  email: '',
  phone: '',
  category: '',
  sn: '',
  model: undefined,
  purchaseDate: '',
  deliveryAgent: undefined,
  country: undefined,
  fullAddress: '',
  faultDescription: '',
  personalData: false,
  policy: false,
};

const CLEARABLE_DELIVERY_FIELDS = [
  'country',
  'fullAddress',
  'buildingNumber',
  'city',
  'apartmentNumber',
  'street',
  'postalCode',
];
const formData = reactive({
  ...DEFAULT_FORM_DATA,
});

const { required, checked, email, phone, alphaSpaces, errorMessage } =
  useValidationRules();

const v$ = useVuelidate(
  {
    name: { required, alphaSpaces },
    email: { required, email },
    phone: { required, phone },
    category: { required },
    sn: { required },
    model: { required },
    purchaseDate: { required },
    deliveryAgent: { required },
    country: { required },
    faultDescription: { required },
    policy: { checked },
    personalData: { checked },
  },
  formData,
);

const deliveryProviderComponent = computed(() => {
  switch (formData.deliveryAgent) {
    case DELIVERY_AGENT.DPD:
      return ServiceFormDpdFields;
    case DELIVERY_AGENT.COURIER:
      return ServiceFormCourierDeliveryFields;
    case DELIVERY_AGENT.NOVA_POSHTA:
      return ServiceFormNovaPoshtaFields;
    default:
      return null;
  }
});

const clearForm = (formFields) => {
  if (formFields?.length) {
    formFields.forEach((fieldName) => {
      formData[fieldName] = DEFAULT_FORM_DATA[fieldName];
    });
  } else {
    Object.assign(formData, DEFAULT_FORM_DATA);
  }
};

const createFormPayload = (formState) => {
  const { deliveryAgent, ...rest } = formState;

  return {
    ...rest,
    deliveryMethod: deliveryAgent,
  };
};

const submitForm = async () => {
  const formId = nuxtState.config.serviceFormUuid;
  const resultFormData = {
    ...createFormPayload(formData),
    page_url: window.location.href,
  };

  await submit(resultFormData, formId);

  $gtm.push({
    event: 'leadform_sent',
  });
  openInfoModal();
  clearForm();
  v$.value.$reset();
};

const handleCountriesLoaded = (countries) => {
  countriesForService.value = countries;

  if (countries.length === 1) {
    formData.country = countries[0].name;
  }
};

const handleSelectCountry = (country) => {
  formData.country = country;
  v$.value.country.$touch();
  clearForm(['fullAddress']);
};

const handleChangeField = ({ fieldName, fieldValue }) => {
  formData[fieldName] = fieldValue;
};

const handleSelectCategory = async (categoryName) => {
  v$.value.category.$touch();
  categorySlug.value = categoryName;

  try {
    await fetchCategory();
  } catch (e) {
    console.error('Fetch category failed: ', e);
  }
};

const pushFormStartEvent = () => {
  if (!isFormStartEventSent.value) {
    $gtm.push({
      event: 'form_start',
    });
  }
  isFormStartEventSent.value = true;
};

const models = computed(() => {
  return category.value?.products?.map((product) => ({
    id: product.id,
    name: product.name,
  }));
});

watch(
  () => formData.deliveryAgent,
  () => {
    clearForm(CLEARABLE_DELIVERY_FIELDS);
    v$.value.country.$reset();
    countriesForService.value = [];
    selectedAddress.value = '';
  },
);

watch(formData, () => {
  pushFormStartEvent();
});

return { __sfc: true,submit, formPending, $recaptcha, isSubmitDisabled, nuxtState, $gtm, store, categorySlug, category, fetchCategory, categories, infoModalIsOpen, openInfoModal, closeInfoModal, config, lang, isFormStartEventSent, selectedAddress, countriesForService, DELIVERY_AGENT, deliveryAgents, DEFAULT_FORM_DATA, CLEARABLE_DELIVERY_FIELDS, formData, required, checked, email, phone, alphaSpaces, errorMessage, v$, deliveryProviderComponent, clearForm, createFormPayload, submitForm, handleCountriesLoaded, handleSelectCountry, handleChangeField, handleSelectCategory, pushFormStartEvent, models }
}

}